import { useState, useCallback } from "react";

type SaveState = "idle" | "uploading" | "processing" | "saved" | "error";

interface SaveRecordingParams {
  blob: Blob;
  title: string;
  savedVideoId: number;
  durationSeconds?: number;
}

export function useSaveRecording() {
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const saveRecording = useCallback(async ({
    blob,
    title,
    savedVideoId,
    durationSeconds,
  }: SaveRecordingParams) => {
    setSaveState("uploading");
    setSaveError(null);

    try {
      // Step 1: Get a Cloudflare Stream direct upload URL
      const urlRes = await fetch("/api/practice-recordings/upload-url", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ durationSeconds }),
      });

      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error((err as any).message || "Failed to get upload URL");
      }

      const { uploadURL, streamId } = await urlRes.json();

      // Step 2: Upload the blob directly to Cloudflare Stream
      const uploadRes = await fetch(uploadURL, {
        method: "POST",
        body: blob,
        headers: { "Content-Type": blob.type || "video/webm" },
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload recording to storage");
      }

      // Step 3: Poll until Cloudflare finishes processing
      setSaveState("processing");
      const thumbnailUrl = await pollUntilReady(streamId);

      // Step 4: Save the record to the database
      const saveRes = await fetch("/api/practice-recordings", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cloudflareStreamId: streamId,
          savedVideoId,
          title,
          durationSeconds,
          thumbnailUrl,
        }),
      });

      if (!saveRes.ok) {
        const err = await saveRes.json().catch(() => ({}));
        throw new Error((err as any).message || "Failed to save recording");
      }

      setSaveState("saved");
    } catch (err: any) {
      setSaveError(err?.message || "Something went wrong");
      setSaveState("error");
    }
  }, []);

  const resetSave = useCallback(() => {
    setSaveState("idle");
    setSaveError(null);
  }, []);

  return { saveState, saveError, saveRecording, resetSave };
}

async function pollUntilReady(streamId: string, maxAttempts = 30): Promise<string | undefined> {
  for (let i = 0; i < maxAttempts; i++) {
    await delay(2000);
    try {
      const res = await fetch(`/api/cloudflare/status/${streamId}`, { credentials: "same-origin" });
      if (!res.ok) continue;
      const data = await res.json();
      if (data.readyToStream) return data.thumbnail ?? undefined;
    } catch {
      // retry
    }
  }
  // Return undefined if it never became ready — we still save the record
  return undefined;
}

function delay(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}
